import { useMemo, useState } from 'react'

type RecordRow = Record<string, unknown>

type Props = {
  records: RecordRow[]
  source: 'online' | 'cache'
  savedAt: string | null
  loading: boolean
  error: string
}

const ARCGIS_URL = 'https://arcgis.aegea.com.br/portal/apps/webappviewer/index.html?id=0cbbe90bebaf4d7a85d07c7af12b0de0'
const SEM_INFO = 'Sem informação na base.'

function text(value: unknown) {
  const result = String(value ?? '').trim()
  return result || SEM_INFO
}

function normalize(value: unknown) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/\s+/g, ' ')
    .trim()
}

function contains(value: unknown, term: string) {
  return normalize(value).includes(normalize(term))
}

function Field({ label, value, emphasis = false }: { label: string; value: unknown; emphasis?: boolean }) {
  return (
    <div className={`empre8-field ${emphasis ? 'emphasis' : ''}`}>
      <span>{label}</span>
      <strong>{text(value)}</strong>
    </div>
  )
}

export function EmpreendimentosCobranca({ records, source, savedAt, loading, error }: Props) {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [copyMessage, setCopyMessage] = useState('')

  const normalizedQuery = normalize(query)
  const matches = useMemo(() => {
    if (!normalizedQuery) return []
    return records
      .filter((row) => normalize(row.empreendimento).includes(normalizedQuery))
      .slice(0, 12)
  }, [records, normalizedQuery])

  const selected = useMemo(() => {
    if (!selectedId) return null
    return records.find((row) => String(row.id ?? row.empreendimento) === selectedId) ?? null
  }, [records, selectedId])

  const hasArcgis = selected ? [
    selected.primeira_cobranca_agua,
    selected.primeira_cobranca_esgoto,
    selected.segunda_cobranca_agua,
    selected.segunda_cobranca_esgoto,
  ].some((value) => contains(value, 'ARCGIS')) : false

  const isLigTerra = selected ? contains(selected.primeira_cobranca_agua, 'LIG. TERRA') : false
  const isColetaTratamento = selected ? contains(selected.primeira_cobranca_esgoto, 'COLETA E TRATAMENTO') : false
  const ramal = selected ? normalize(selected.ramal_rede) : ''
  const redeCalcada = ramal.includes('rede na calcada')
  const ramalEspera = !redeCalcada && ramal.includes('sim')
  const secondNotApplicable = selected ? (
    contains(selected.segunda_cobranca_agua, 'NÃO SE APLICA') ||
    contains(selected.segunda_cobranca_esgoto, 'NÃO SE APLICA')
  ) : false

  function select(row: RecordRow) {
    setSelectedId(String(row.id ?? row.empreendimento))
    setQuery(String(row.empreendimento ?? ''))
    setCopyMessage('')
  }

  function clear() {
    setQuery('')
    setSelectedId(null)
    setCopyMessage('')
  }

  async function copySummary() {
    if (!selected) return
    const lines = [
      `EMPREENDIMENTO: ${text(selected.empreendimento)}`,
      `TIPO DE LOTEAMENTO: ${text(selected.tipo_loteamento)}`,
      `1ª COBRANÇA ÁGUA: ${text(selected.primeira_cobranca_agua)}`,
      `1ª COBRANÇA ESGOTO: ${text(selected.primeira_cobranca_esgoto)}`,
      `2ª COBRANÇA ÁGUA: ${text(selected.segunda_cobranca_agua)}`,
      `2ª COBRANÇA ESGOTO: ${text(selected.segunda_cobranca_esgoto)}`,
      `RAMAL / REDE: ${text(selected.ramal_rede)}`,
    ]
    if (isLigTerra) lines.push('OS ÁGUA: 117065 — FINANC. IMPLANTAÇÃO LIGAÇÃO ÁGUA ASFALTO')
    if (isColetaTratamento) lines.push('OS ESGOTO: 102003 — COLETA E TRATAMENTO DE ESGOTO - ATIVAÇÃO')
    if (redeCalcada) lines.push('ORIENTAÇÃO: Rede na calçada — cobrança Água Terra e Esgoto Terra independentemente da quantidade de casas no lote.')
    else if (ramalEspera) lines.push('ORIENTAÇÃO: Ramal de espera — cobrança diferenciada como Ligação Terra somente para a primeira ligação do lote.')
    if (secondNotApplicable) lines.push('2º PEDIDO: NÃO SE APLICA — seguir procedimento normal conforme o pavimento existente.')
    if (hasArcgis) lines.push('ARCGIS: consultar rede e pavimento antes de definir a cobrança.')

    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      setCopyMessage('Resumo copiado.')
    } catch {
      setCopyMessage('Não foi possível copiar automaticamente.')
    }
  }

  return (
    <section className="operational-tool empre8">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL</p>
          <h2>Empreendimentos / Cobrança</h2>
          <p className="muted">Localize o empreendimento e consulte regras de 1ª cobrança, 2º pedido, ramal/rede e orientações vinculadas.</p>
        </div>
        <span className={`status-chip ${source === 'cache' ? 'warning' : ''}`}>{source === 'cache' ? 'Contingência offline' : 'Base publicada'}</span>
      </div>

      <section className="empre8-search-card">
        <div className="empre8-search-head">
          <div><strong>Buscar empreendimento</strong><span>{records.length} registros disponíveis{source === 'cache' && savedAt ? ` · cache ${new Date(savedAt).toLocaleString('pt-BR')}` : ''}</span></div>
          <button className="secondary-button compact" type="button" onClick={clear}>Limpar</button>
        </div>
        <input
          type="search"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setSelectedId(null); setCopyMessage('') }}
          placeholder="Digite o nome ou parte do condomínio/loteamento..."
          autoComplete="off"
        />

        {loading && <div className="empty-state">Carregando empreendimentos...</div>}
        {error && <div className="notice error">{error}</div>}
        {!loading && !error && normalizedQuery && matches.length === 0 && <div className="empty-state">Nenhum empreendimento encontrado.</div>}
        {!selected && matches.length > 0 && (
          <div className="empre8-results">
            {matches.map((row) => (
              <button type="button" key={String(row.id ?? row.empreendimento)} onClick={() => select(row)}>
                <strong>{text(row.empreendimento)}</strong>
                <span>{text(row.tipo_loteamento)} · Ramal/Rede: {text(row.ramal_rede)}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {!selected && !loading && !error && !normalizedQuery && (
        <div className="empre8-empty">
          <strong>Comece digitando o nome do empreendimento.</strong>
          <span>A busca ignora acentos, maiúsculas/minúsculas e aceita parte do nome.</span>
        </div>
      )}

      {selected && (
        <>
          <section className="empre8-identity">
            <div><p className="eyebrow">EMPREENDIMENTO SELECIONADO</p><h3>{text(selected.empreendimento)}</h3></div>
            <div className="empre8-identity-tags"><span>{text(selected.tipo_loteamento)}</span><span>{text(selected.ramal_rede)}</span></div>
          </section>

          <div className="empre8-grid">
            <section className="empre8-panel">
              <div className="empre8-panel-head"><div><span>1</span><strong>1ª Cobrança</strong></div><small>Primeira ligação / implantação</small></div>
              <div className="empre8-fields">
                <Field label="Água" value={selected.primeira_cobranca_agua} emphasis />
                <Field label="Esgoto" value={selected.primeira_cobranca_esgoto} emphasis />
              </div>
              {(isLigTerra || isColetaTratamento || hasArcgis) && (
                <div className="empre8-actions-stack">
                  {isLigTerra && <div className="empre8-action"><span>OS Água</span><strong>117065</strong><small>FINANC. IMPLANTAÇÃO LIGAÇÃO ÁGUA ASFALTO</small></div>}
                  {isColetaTratamento && <div className="empre8-action"><span>OS Esgoto</span><strong>102003</strong><small>COLETA E TRATAMENTO DE ESGOTO - ATIVAÇÃO</small></div>}
                  {hasArcgis && <a className="primary-button compact empre8-arcgis" href={ARCGIS_URL} target="_blank" rel="noreferrer">Abrir ArcGIS · rede e pavimento ↗</a>}
                </div>
              )}
            </section>

            <section className="empre8-panel">
              <div className="empre8-panel-head"><div><span>2</span><strong>2º Pedido / Ponto adicional</strong></div><small>Procedimento distinto da 1ª implantação</small></div>
              <div className="empre8-fields">
                <Field label="Água" value={selected.segunda_cobranca_agua} emphasis />
                <Field label="Esgoto" value={selected.segunda_cobranca_esgoto} emphasis />
              </div>
              {secondNotApplicable && <div className="notice info empre8-guidance">“NÃO SE APLICA” na 2ª cobrança: seguir procedimento normal conforme o pavimento existente.</div>}
            </section>
          </div>

          <section className="empre8-panel">
            <div className="empre8-panel-head"><div><span>3</span><strong>Ramal / Rede e orientação final</strong></div><small>Regra operacional associada ao empreendimento</small></div>
            <div className="empre8-summary-grid">
              <Field label="Ramal / Rede" value={selected.ramal_rede} />
              <Field label="Tipo de loteamento" value={selected.tipo_loteamento} />
            </div>
            <div className="empre8-guidance-list">
              {redeCalcada && <div className="notice info">Rede na calçada: orientar cobrança <strong>Água Terra e Esgoto Terra</strong>, independentemente da quantidade de casas no lote.</div>}
              {ramalEspera && <div className="notice info">Ramal de espera: cobrança diferenciada como <strong>Ligação Terra somente para a primeira ligação do lote</strong>.</div>}
              {!redeCalcada && !ramalEspera && normalize(selected.ramal_rede) === '' && <div className="notice info">Ramal / Rede: sem informação na base. Não inferir regra; seguir consulta operacional aplicável.</div>}
              {hasArcgis && <div className="notice info">A base determina consulta ao <strong>ArcGIS</strong> para confirmar rede e pavimento antes de definir a cobrança.</div>}
            </div>
            <div className="empre8-footer-actions">
              <button className="secondary-button" type="button" onClick={() => void copySummary()}>Copiar resumo</button>
              {copyMessage && <span>{copyMessage}</span>}
            </div>
          </section>
        </>
      )}
    </section>
  )
}
